import { orderByRole, resolveRoles } from "./roles";
import type {
  DesignPlant,
  DiagramSpot,
  Placement,
  Planter,
  PlantRole,
  PlantingPlan,
  RecipePlant,
} from "./types";

/**
 * Deterministic planting plan for a planter + container recipe.
 *
 * No model places anything: the positions are a fixed template driven only by
 * the planter's shape and inner dimensions, so the diagram never drifts between
 * generations and the plant counts on the receipt are exactly the dots on the
 * diagram. Coordinates are inches from the planter center, +y toward the
 * FRONT (the viewer).
 *
 *   freestanding  thriller(s) on the center line, fillers in rings/rows around,
 *                 spillers all the way round the rim.
 *   wall          thriller(s) at the back against the wall, fillers stepping
 *                 down toward the viewer, spillers on the front rim only.
 */

/** Footprint radii at planting for a planter 24"+ across: a 1 gal thriller, 4" fillers and spillers. */
export const FOOTPRINT: Record<PlantRole, number> = { thriller: 5, filler: 2.6, spiller: 2.6 };
export const POT_SIZE: Record<PlantRole, string> = { thriller: "1 Gal", filler: "4 In", spiller: "4 In" };
/** Centers on a path, inches (before scaling): spillers trail 16" apart on a
 *  freestanding rim, 12" on a front rim; plugs sit 5" apart. */
const FILLER_SPACING = 5;
const RIM_SPACING = { freestanding: 16, wall: 12 } as const;

/** Badge colors by diagram number; fixed so a design re-renders identically. */
export const PLANT_COLORS = [
  "#8e3fa6", // plum
  "#c9a227", // gold
  "#8b9c96", // sage grey
  "#5aae2e", // leaf green
  "#d0566c", // rose
  "#3b7fa8", // slate blue
  "#dd7a2f", // terracotta
  "#6c5a7d", // dusk
];

export function innerHalfExtents(p: Planter): { hx: number; hy: number; round: boolean } {
  if (p.shape === "round") {
    const r = Math.max(((p.diameterIn ?? 12) - 1) / 2, 3);
    return { hx: r, hy: r, round: true };
  }
  return {
    hx: Math.max(((p.lengthIn ?? 12) - 1) / 2, 3),
    hy: Math.max(((p.widthIn ?? p.lengthIn ?? 12) - 1) / 2, 2.5),
    round: false,
  };
}

/** Small planters get proportionally smaller footprints and spacing (a 12" pot
 *  is planted tighter than a 36" one), never below 55% of the full size. */
export function footprintScale(p: Planter): number {
  const { hx, hy } = innerHalfExtents(p);
  return Math.min(1, Math.max(0.55, Math.min(hx, hy) / 12));
}

type Slot = { role: PlantRole; x: number; y: number };

const round2 = (n: number) => Math.round(n * 100) / 100;

// ── slot generation ───────────────────────────────────────────────────────

/**
 * Evenly spaced points on the soil outline inset by `inset`: a circle for a
 * round pot, a rectangle otherwise. Walks clockwise from the front center,
 * shifted half a step so no point sits dead center front.
 */
function outlinePoints(planter: Planter, inset: number, spacing: number, minCount: number): { x: number; y: number }[] {
  const { hx, hy, round } = innerHalfExtents(planter);
  if (round) {
    const r = hx - inset;
    if (r <= 0.5) return [];
    const n = Math.max(minCount, Math.floor((2 * Math.PI * r) / spacing));
    return Array.from({ length: n }, (_, k) => {
      const a = Math.PI / 2 + Math.PI / n + (2 * Math.PI * k) / n;
      return { x: r * Math.cos(a), y: r * Math.sin(a) };
    });
  }
  const a = hx - inset;
  const b = hy - inset;
  if (a < 0 || b < 0) return [];
  const per = 4 * (a + b);
  if (per < 1) return [{ x: 0, y: 0 }];
  const n = Math.max(minCount, Math.floor(per / spacing));
  const at = (t: number) => {
    // Perimeter parameter t ∈ [0, per) from front center (0, b), clockwise
    // as seen from above with the front at the bottom.
    let d = (t + a) % per; // shift origin to the front-left corner
    if (d < 2 * a) return { x: -a + d, y: b };
    d -= 2 * a;
    if (d < 2 * b) return { x: a, y: b - d };
    d -= 2 * b;
    if (d < 2 * a) return { x: a - d, y: -b };
    d -= 2 * a;
    return { x: -a, y: -b + d };
  };
  return Array.from({ length: n }, (_, k) => at(((k + 0.5) * per) / n));
}

function thrillerSlots(planter: Planter, placement: Placement, s: number): Slot[] {
  const { hx, hy, round } = innerHalfExtents(planter);
  const r = FOOTPRINT.thriller * s;
  if (round) {
    if (placement === "wall") return [{ role: "thriller", x: 0, y: -Math.max(hy - r - 1.5 * s, 0) }];
    if (hx >= 22) {
      // A big pot (45"+) gets a triangle of thrillers so the center isn't a single stem.
      return [0, 1, 2].map((k) => {
        const a = -Math.PI / 2 + (k * 2 * Math.PI) / 3;
        return { role: "thriller" as const, x: 5.5 * Math.cos(a), y: 5.5 * Math.sin(a) };
      });
    }
    return [{ role: "thriller", x: 0, y: 0 }];
  }
  const n = Math.max(1, Math.round((2 * hx) / 24));
  const y = placement === "wall" ? -Math.max(hy - r - 0.5, 0) : 0;
  return Array.from({ length: n }, (_, i) => ({ role: "thriller" as const, x: -hx + (2 * hx * (i + 0.5)) / n, y }));
}

function clearOf(x: number, y: number, taken: Slot[], s: number): boolean {
  return taken.every(
    (t) => Math.hypot(t.x - x, t.y - y) >= (FOOTPRINT[t.role] + FOOTPRINT.filler) * s - (t.role === "thriller" ? 0 : 0.6 * s)
  );
}

/**
 * Spillers on the rim (all the way round freestanding, front half against a
 * wall), then fillers on outlines stepping inward until they reach the
 * thriller. Against a wall the back half of the rim holds fillers instead of
 * spillers: nothing should trail down a wall nobody sees.
 */
function rimAndFillerSlots(planter: Planter, placement: Placement, thrillers: Slot[], s: number): Slot[] {
  const rimInset = (FOOTPRINT.spiller + 0.3) * s;
  const rim = outlinePoints(planter, rimInset, RIM_SPACING[placement] * s, placement === "wall" ? 4 : 3);
  const out: Slot[] = [];
  for (const p of rim) {
    const front = p.y >= -0.01;
    if (placement === "freestanding" || front) out.push({ role: "spiller", ...p });
  }
  if (placement === "wall") {
    // Back rim: fillers at plug spacing, wherever the thriller leaves room.
    for (const p of outlinePoints(planter, rimInset, FILLER_SPACING * s, 3)) {
      if (p.y < -0.01 && clearOf(p.x, p.y, [...thrillers, ...out], s)) out.push({ role: "filler", ...p });
    }
  }
  const step = (2 * FOOTPRINT.filler + 0.8) * s;
  for (let inset = rimInset + step; ; inset += step) {
    const ring = outlinePoints(planter, inset, FILLER_SPACING * s, 3);
    if (!ring.length) break;
    const kept = ring.filter((p) => clearOf(p.x, p.y, [...thrillers, ...out], s));
    out.push(...kept.map((p) => ({ role: "filler" as const, ...p })));
    if (ring.length <= 3) break;
  }
  if (!out.some((o) => o.role === "filler")) {
    // No room for an inner ring (a small pot): one filler on the rim halfway
    // between each pair of neighbouring spillers instead.
    const sp = out.filter((o) => o.role === "spiller");
    const { round } = innerHalfExtents(planter);
    const closed = placement === "freestanding";
    for (let i = 0; i < sp.length - (closed ? 0 : 1); i++) {
      const a = sp[i];
      const b = sp[(i + 1) % sp.length];
      let x = (a.x + b.x) / 2;
      let y = (a.y + b.y) / 2;
      if (round) {
        const rr = Math.hypot(a.x, a.y);
        const ang = Math.atan2(y, x);
        x = rr * Math.cos(ang);
        y = rr * Math.sin(ang);
      }
      out.push({ role: "filler", x, y });
    }
  }
  return out;
}

// ── species assignment ────────────────────────────────────────────────────

/** Angle from the back-left going clockwise, so groups read as drifts around the pot. */
function sweepAngle(s: Slot): number {
  const a = Math.atan2(s.y, s.x) + Math.PI / 2 + Math.PI / 4;
  return ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

/** Split `n` slots between species: the first filler is the base (weight 2), the rest accents (1). */
function apportion(n: number, species: number, weighted: boolean): number[] {
  if (species === 0) return [];
  const w = Array.from({ length: species }, (_, i) => (weighted && i === 0 && species > 1 ? 2 : 1));
  const total = w.reduce((a, b) => a + b, 0);
  const out = w.map((x) => Math.floor((n * x) / total));
  let left = n - out.reduce((a, b) => a + b, 0);
  for (let i = 0; left > 0; i = (i + 1) % species, left--) out[i]++;
  // Every species shows up at least once when there is room.
  for (let i = 0; i < species; i++) {
    if (out[i] === 0) {
      const donor = out.indexOf(Math.max(...out));
      if (out[donor] > 1) {
        out[donor]--;
        out[i]++;
      }
    }
  }
  return out;
}

export interface ContainerDesign {
  plants: DesignPlant[];
  plans: Record<Placement, PlantingPlan>;
}

/**
 * Build the numbered plant list and BOTH plans. Counts on `plants` follow the
 * freestanding plan (the default view); `plans.wall.counts` carries the wall
 * counts, which can differ by a plant or two.
 */
export function buildContainerDesign(
  planter: Planter,
  recipePlants: RecipePlant[],
  authoredRoles: Record<string, PlantRole> | null | undefined
): ContainerDesign {
  const roles = resolveRoles(recipePlants, authoredRoles);
  const ordered = orderByRole(recipePlants, roles);
  const numbered = ordered.map((p, i) => ({
    ...p,
    number: i + 1,
    role: roles[p.id],
    potSize: POT_SIZE[roles[p.id]],
    color: PLANT_COLORS[i % PLANT_COLORS.length],
  }));

  const plans = {} as Record<Placement, PlantingPlan>;
  for (const placement of ["freestanding", "wall"] as Placement[]) {
    const scale = footprintScale(planter);
    const thrillers = thrillerSlots(planter, placement, scale);
    const rest = rimAndFillerSlots(planter, placement, thrillers, scale);
    const spillers = rest.filter((r) => r.role === "spiller");
    const fillers = rest.filter((r) => r.role === "filler");

    const byRole = (r: PlantRole) => numbered.filter((p) => p.role === r);
    // Slots of a role with no species fall to a neighbour role rather than
    // being left as bare soil: fillers → spillers → thriller.
    const owner = (r: PlantRole): PlantRole => {
      const chain: Record<PlantRole, PlantRole[]> = {
        thriller: ["thriller", "filler", "spiller"],
        filler: ["filler", "spiller", "thriller"],
        spiller: ["spiller", "filler", "thriller"],
      };
      return chain[r].find((c) => byRole(c).length) ?? r;
    };

    const spots: DiagramSpot[] = [];
    const place = (slots: Slot[], role: PlantRole, drifts: boolean) => {
      const species = byRole(owner(role));
      if (!species.length || !slots.length) return;
      const sorted = drifts ? [...slots].sort((a, b) => sweepAngle(a) - sweepAngle(b)) : slots;
      if (drifts) {
        const counts = apportion(sorted.length, species.length, role === "filler");
        let i = 0;
        species.forEach((sp, k) => {
          for (let c = 0; c < counts[k]; c++, i++) {
            const s = sorted[i];
            spots.push({ number: sp.number, role, x: round2(s.x), y: round2(s.y), r: round2(FOOTPRINT[role] * scale) });
          }
        });
      } else {
        // thrillers and spillers alternate species so each reads evenly.
        sorted.forEach((s, i) => {
          const sp = species[i % species.length];
          spots.push({ number: sp.number, role, x: round2(s.x), y: round2(s.y), r: round2(FOOTPRINT[role] * scale) });
        });
      }
    };
    place(thrillers, "thriller", false);
    place(fillers, "filler", true);
    place(spillers, "spiller", false);

    const counts: Record<number, number> = {};
    for (const s of spots) counts[s.number] = (counts[s.number] ?? 0) + 1;
    plans[placement] = { placement, spots, counts };
  }

  const plants: DesignPlant[] = numbered.map((p) => ({ ...p, count: plans.freestanding.counts[p.number] ?? 0 }));
  return { plants, plans };
}

/** Plants (with counts) for a given placement. */
export function plantsFor(design: ContainerDesign, placement: Placement): DesignPlant[] {
  return design.plants.map((p) => ({ ...p, count: design.plans[placement].counts[p.number] ?? 0 }));
}
